// Accessibility Announcer Class - manages all screen reader announcements to prevent conflicts
class AccessibilityAnnouncer {
    constructor(liveRegionId) {
        this.liveRegion = document.getElementById(liveRegionId);
        this.activeMessages = new Map(); // Map of messageKey -> messageText
        this.announceTimeout = null;
    }

    /**
     * Add or update a message with a specific key
     * @param {string} messageKey - Unique key for this message type
     * @param {string} messageText - The message to announce
     */
    addMessage(messageKey, messageText) {
        if (!messageText || messageText.trim() === '') {
            this.removeMessage(messageKey);
            return;
        }
        this.activeMessages.set(messageKey, messageText);
        this.announce();
    }

    /**
     * Remove a message by key
     * @param {string} messageKey - The key of the message to remove
     */
    removeMessage(messageKey) {
        this.activeMessages.delete(messageKey);
        this.announce();
    }

    /**
     * Clear all messages
     */
    clearAll() {
        this.activeMessages.clear();
        this.announce();
    }

    /**
     * Combine all active messages and announce them
     */
    announce() {
        if (!this.liveRegion) return;

        // Clear any pending announcements
        if (this.announceTimeout) {
            clearTimeout(this.announceTimeout);
        }

        // Combine all active messages
        const combinedMessage = Array.from(this.activeMessages.values())
            .filter(msg => msg && msg.trim() !== '')
            .join(' ');

        // Clear and set to trigger announcement
        this.liveRegion.textContent = '';
        this.announceTimeout = setTimeout(() => {
            this.liveRegion.textContent = combinedMessage || '';
        }, 50);
    }
}

// Initialize the accessibility announcer
const accessibilityAnnouncer = new AccessibilityAnnouncer('address-status');

// Accessibility announcements - translations should be added to window.theme.strings.storelocator.accessibility
const announcements = {
    townsRegionsFound: (count) => {
        const strings = window.theme?.strings?.accessibility?.storelocator;
        if (strings?.townsRegionsFound) {
            const plural = count !== 1 ? 's' : '';
            return strings.townsRegionsFound
                .replace(/\[count\]/g, count)
                .replace(/\[plural\]/g, plural);
        }
        return `${count} town${count !== 1 ? 's' : ''} or region${count !== 1 ? 's' : ''} found. Use up and down arrows to navigate.`;
    },
    noTownsRegionsFound: () => {
        const strings = window.theme?.strings?.accessibility?.storelocator;
        return strings?.noTownsRegionsFound || 'No towns or regions found.';
    },
    noResultsFound: () => {
        const strings = window.theme?.strings?.accessibility?.noResultsFound;
        return strings?.error || 'No results found';
    },
    shopLocationsFound: (count) => {
        const strings = window.theme?.strings?.accessibility?.storelocator;
        if (strings?.shopLocationsFound) {
            const plural = count !== 1 ? 's' : '';
            return strings.shopLocationsFound
                .replace(/\[count\]/g, count)
                .replace(/\[plural\]/g, plural);
        }
        return `${count} shop location${count !== 1 ? 's' : ''} found`;
    },
    storeLocationsFound: (count) => {
        const strings = window.theme?.strings?.accessibility?.storelocator;
        if (strings?.storeLocationsFound) {
            const plural = count !== 1 ? 's' : '';
            return strings.storeLocationsFound
                .replace(/\[count\]/g, count)
                .replace(/\[plural\]/g, plural);
        }
        return `${count} store location${count !== 1 ? 's' : ''} found. Use arrow keys to navigate.`;
    },
    detailsExpanded: () => {
        const strings = window.theme?.strings?.accessibility?.storelocator;
        return strings?.detailsExpanded || 'details expanded';
    },
    detailsCollapsed: () => {
        const strings = window.theme?.strings?.accessibility?.storelocator;
        return strings?.detailsCollapsed || 'details collapsed';
    },
    pressToExpand: () => {
        const strings = window.theme?.strings?.accessibility?.storelocator;
        return strings?.pressToExpand || 'Press Enter or Space to expand for details.';
    },
    locationAway: (name, distance, unit, isUltra) => {
        const strings = window.theme?.strings?.accessibility?.storelocator;
        const ultraText = isUltra ? (strings?.ultraLocation || ', Vuse Ultra location') : '';
        if (strings?.locationAway) {
            return strings.locationAway
                .replace('[name]', name)
                .replace('[distance]', distance)
                .replace('[unit]', unit)
                .replace('[ultra]', ultraText);
        }
        return `${name}, ${distance} ${unit} away${ultraText}`;
    }
};

const storeLocatorData = document.querySelector('.store-locator');
const dataMapZoom = storeLocatorData.getAttribute('data-map-zoom') ?? 5.5;
const dataMapLongitude = parseFloat(storeLocatorData.getAttribute('data-map-long')) ?? -6.24827;
const dataMapLatitude = parseFloat(storeLocatorData.getAttribute('data-map-lat')) ?? 53.34807;
const dataMapDistanceUnit = storeLocatorData.getAttribute('data-distance');

let currentPopup = null;
const allMarkers = Array.isArray(window.theme.store_locator.additionalLocationMarkers)
    ? window.theme.store_locator.markers.concat(window.theme.store_locator.additionalLocationMarkers)
    : window.theme.store_locator.markers;
const mapFeatures = [];
let idCounter = 0;
let activeResultsContainer = getActiveResultsContainer();
let selectedFeatureId = null;
let activeFilter = 'all';
let lastSearchCoords = null;
let selectedMarker = null;

function getActiveResultsContainer() {
    return window.innerWidth > 1024 ? document.getElementById('search-results') : document.getElementById('search-results-mobile');
}

mapboxgl.accessToken = 'pk.eyJ1IjoibS13aXNlbWFuIiwiYSI6ImNrNXBtMWFiMDAyeDIza210cGFjejFib2wifQ.5cyAEUHndVPYPo9CV29x1w';
const map = new mapboxgl.Map({
    container: 'map',
    style: 'mapbox://styles/mapbox/light-v11',
    center: [dataMapLongitude, dataMapLatitude],
    zoom: dataMapZoom,
});

function getClusterFilter() {
    if (activeFilter === 'ultra') {
        return ['all', ['has', 'point_count'], ['>', ['get', 'ultra_ambassador_count'], 0]];
    }
    if (activeFilter === 'non-ultra') {
        return ['all', ['has', 'point_count'], ['>', ['get', 'non_ultra_count'], 0]];
    }
    return ['has', 'point_count'];
}

function getUnclusteredPointsFilter() {
    if (activeFilter === 'ultra') {
        return ['all', ['!', ['has', 'point_count']], ['==', ['get', 'ultraAmbassador'], true]];
    }
    if (activeFilter === 'non-ultra') {
        return ['all', ['!', ['has', 'point_count']], ['!=', ['get', 'ultraAmbassador'], true]];
    }
    return ['!', ['has', 'point_count']];
}

function getUnclusteredPointsFilterExcludingSelected(baseFilter) {
    if (selectedFeatureId === null || typeof selectedFeatureId === 'undefined') {
        return baseFilter;
    }
    return ['all', baseFilter, ['!=', ['id'], selectedFeatureId]];
}

function applyMapFilter() {
    if (!map.getSource('stores')) return;

    map.setFilter('clusters', getClusterFilter());
    map.setFilter('cluster-count', getClusterFilter());

    const baseUnclusteredFilter = getUnclusteredPointsFilter();
    map.setFilter('unclustered-point', getUnclusteredPointsFilterExcludingSelected(baseUnclusteredFilter));
    map.setFilter('unclustered-point-selected', ['==', ['id'], selectedFeatureId ?? -1]);

    try {
        if (activeFilter === 'ultra') {
            map.setPaintProperty('clusters', 'circle-color', '#E6B843');
            map.setPaintProperty('clusters', 'circle-stroke-color', '#AE8E37');
            map.setLayoutProperty('cluster-count', 'text-field', ['number-format', ['get', 'ultra_ambassador_count'], {}]);
            map.setPaintProperty('cluster-count', 'text-color', '#2b2b2b');
        } else if (activeFilter === 'non-ultra') {
            map.setPaintProperty('clusters', 'circle-color', [
                'step', ['get', 'point_count'], '#333333', 100, '#333333', 750, '#333333'
            ]);
            map.setPaintProperty('clusters', 'circle-stroke-color', 'lightgray');
            map.setLayoutProperty('cluster-count', 'text-field', ['number-format', ['get', 'non_ultra_count'], {}]);
            map.setPaintProperty('cluster-count', 'text-color', '#ffffff');
        } else {
            map.setPaintProperty('clusters', 'circle-color', [
                'case',
                ['>', ['get', 'ultra_ambassador_count'], 0],
                '#E6B843',
                ['step', ['get', 'point_count'], '#333333', 100, '#333333', 750, '#333333']
            ]);
            map.setPaintProperty('clusters', 'circle-stroke-color', [
                'case',
                ['>', ['get', 'ultra_ambassador_count'], 0],
                '#AE8E37',
                'lightgray'
            ]);
            map.setLayoutProperty('cluster-count', 'text-field', [
                'concat',
                ['number-format', ['get', 'non_ultra_count'], {}],
                '\n',
                ['case',
                    ['>', ['get', 'ultra_ambassador_count'], 0],
                    ['concat', '🟡', ['number-format', ['get', 'ultra_ambassador_count'], {}]],
                    ''
                ]
            ]);
            map.setPaintProperty('cluster-count', 'text-color', [
                'case',
                ['>', ['get', 'ultra_ambassador_count'], 0],
                '#2b2b2b',
                '#ffffff'
            ]);
        }
    } catch (e) {
        // Layers not ready yet
    }
}

map.on('load', async () => {
    allMarkers.forEach(marker => {
        marker.id = idCounter++;
        mapFeatures.push({
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [marker.Longitude, marker.Latitude]
            },
            properties: {
                id: marker.id,
                name: marker.Name,
                addressLine1: marker['Address Line 1'],
                addressLine2: marker['Address Line 2'],
                city: marker['City/Town'],
                postalCode: marker['Postal Code'],
                ultraAmbassador: marker.ultraAmbassador || false
            }
        });
    });

    map.addSource('stores', {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features: mapFeatures
        },
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
        generateId: true,
        clusterProperties: {
            ultra_ambassador_count: ['+', ['case', ['get', 'ultraAmbassador'], 1, 0]],
            non_ultra_count: ['+', ['case', ['get', 'ultraAmbassador'], 0, 1]]
        }
    });

    map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'stores',
        filter: ['has', 'point_count'],
        paint: {
            'circle-color': [
                'case',
                ['>', ['get', 'ultra_ambassador_count'], 0],
                '#E6B843',
                ['step', ['get', 'point_count'], '#333333', 100, '#333333', 750, '#333333']
            ],
            'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
            'circle-stroke-color': [
                'case',
                ['>', ['get', 'ultra_ambassador_count'], 0],
                '#AE8E37',
                'lightgray'
            ],
            'circle-stroke-width': 4
        }
    });

    map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'stores',
        filter: ['has', 'point_count'],
        layout: {
            'text-field': [
                'concat',
                ['number-format', ['get', 'non_ultra_count'], {}],
                '\n',
                ['case',
                    ['>', ['get', 'ultra_ambassador_count'], 0],
                    ['concat', '🟡', ['number-format', ['get', 'ultra_ambassador_count'], {}]],
                    ''
                ]
            ],
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 12,
            'text-line-height': 1.2,
        },
        paint: {
            "text-color": [
                'case',
                ['>', ['get', 'ultra_ambassador_count'], 0],
                '#2b2b2b',
                '#ffffff'
            ]
        }
    });

    if (window.theme.store_locator.markerImage != '')
        await map.loadImage(window.theme.store_locator.markerImage, (error, image) => {
            if (error) throw error;
            map.addImage('vuseIcon', image);
        });

    if (window.theme.store_locator.markerImageSelected != '')
        await map.loadImage(window.theme.store_locator.markerImageSelected, (error, selectedImage) => {
            if (error) throw error;
            map.addImage('vuseIconSelected', selectedImage);
        });

    if (window.theme.store_locator.goldMarkerImage != '')
        await map.loadImage(window.theme.store_locator.goldMarkerImage, (error, goldImage) => {
            if (error) throw error;
            map.addImage('vuseGoldIcon', goldImage);
        });

    map.addLayer({
        id: 'unclustered-point',
        type: 'symbol',
        source: 'stores',
        filter: ['!', ['has', 'point_count']],
        layout: {
            'icon-image': [
                'case',
                ['get', 'ultraAmbassador'],
                'vuseGoldIcon',
                'vuseIcon'
            ],
            'icon-size': 0.25
        }
    });

    map.addLayer({
        id: 'unclustered-point-selected',
        type: 'symbol',
        source: 'stores',
        filter: ['==', ['id'], selectedFeatureId],
        layout: {
            'icon-image': [
                'case',
                ['get', 'ultraAmbassador'],
                'vuseGoldIcon',
                'vuseIconSelected'
            ],
            'icon-size': 0.25
        }
    });

    applyMapFilter();

    map.on('click', 'clusters', (e) => {
        const features = map.queryRenderedFeatures(e.point, {
            layers: ['clusters']
        });
        const clusterId = features[0].properties.cluster_id;
        map.getSource('stores').getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;
            map.easeTo({
                center: features[0].geometry.coordinates,
                zoom: zoom
            });
        });
    });

    map.on('click', 'unclustered-point', (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        const { name, addressLine1, addressLine2, city, postalCode, ultraAmbassador } = e.features[0].properties;
        const addressParts = [];

        if (addressLine1) addressParts.push(addressLine1);
        if (addressLine2) addressParts.push(addressLine2);
        if (city) addressParts.push(city);
        if (postalCode) addressParts.push(postalCode);

        const googleMapsURL = `https://www.google.com/maps/dir/?api=1&destination=${coordinates[1]},${coordinates[0]}`;
        const ultraBanner = ultraAmbassador ? '<div class="ultra-banner">Vuse Ultra</div>' : '';
        const popupHTML = `
            <div class="location-popup">
                ${ultraBanner}
                <div class="location-popup__content">
                    <strong>${name}</strong><br>
                    <p>${addressParts.join(', ')}</p>
                    <button class="btn btn-primary direction" onclick="window.open('${googleMapsURL}', '_blank')">${window.theme.strings.storelocator.direction}</button>
                </div>
            </div>
        `;

        const popup = new mapboxgl.Popup()
            .setLngLat(coordinates)
            .setHTML(popupHTML)
            .addTo(map);

        popup.on('close', () => {
            selectedFeatureId = null;
            applyMapFilter();
            removeSelectedMarker();
        });

        selectedFeatureId = e.features[0].id;
        applyMapFilter();
    });

    map.on('click', (e) => {
        if (e.features && e.features.length > 0) {
            const clickedOnPoint = e.features.some(feature => feature.layer.id === 'unclustered-point');
            if (!clickedOnPoint) {
                selectedFeatureId = null;
                applyMapFilter();
            }
        }
    });
});

let countryCode = theme.settings.countryCode;
if (countryCode === "FR") {
    countryCode = "FR,MC";
}

const geocoder = new MapboxGeocoder({
    accessToken: mapboxgl.accessToken,
    mapboxgl: mapboxgl,
    marker: false,
    flyTo: false,
    container: 'geocoder-container',
    countries: countryCode ?? 'IE'
});

document.getElementById('geocoder-container').appendChild(geocoder.onAdd(map));

const addressStatusElement = document.getElementById('address-status');
const addressInput = document.querySelector('.mapboxgl-ctrl-geocoder--input');
addressInput.placeholder = window.theme.strings.townPostalCodeText;
addressInput.removeAttribute("aria-label");
addressInput.removeAttribute("placeholder")
addressInput.setAttribute("id", "address-lookup-input");
addressInput.setAttribute("aria-haspopup", "listbox");
addressInput.setAttribute("role", "combobox");
addressInput.setAttribute("aria-expanded", "false");
addressInput.setAttribute("aria-autocomplete", "list");
addressInput.setAttribute("aria-controls", "address-lookup-results");

const suggestionsList = document.querySelector('.suggestions-wrapper ul');
suggestionsList.setAttribute('id', "address-lookup-results");
suggestionsList.setAttribute('role', 'listbox');

if (suggestionsList) {
    let lastActiveOptionId = null;

    const updateSuggestionsAriaAttributes = () => {
        let activeOptionId = null;
        Array.from(suggestionsList.children).forEach((li, index) => {
            // Ensure each option has a unique ID
            if (!li.id) {
                li.id = `address-option-${index}`;
            }
            li.setAttribute('role', 'option');

            // Get the text content for accessibility
            const optionText = li.textContent.trim() || li.innerText.trim();
            if (optionText && !li.getAttribute('aria-label')) {
                li.setAttribute('aria-label', optionText);
            }

            if (li.classList.contains('active')) {
                li.setAttribute('aria-selected', 'true');
                activeOptionId = li.id;
            } else {
                li.setAttribute('aria-selected', 'false');
            }
        });

        // Update aria-activedescendant on the input to point to the active option
        if (activeOptionId) {
            addressInput.setAttribute('aria-activedescendant', activeOptionId);

            // Only announce if the active option has changed
            if (activeOptionId !== lastActiveOptionId) {
                lastActiveOptionId = activeOptionId;

                // Announce the highlighted option - get the full text from the option element
                const activeOption = suggestionsList.querySelector('.active');
                if (activeOption) {
                    // Get the full text content, including any nested elements
                    let optionText = '';
                    // Try to get text from the main content area (Mapbox geocoder structure)
                    const suggestionContent = activeOption.querySelector('.mapboxgl-ctrl-geocoder--suggestion-main') ||
                        activeOption.querySelector('.suggestion') ||
                        activeOption;

                    // Get all text nodes, excluding any icons or buttons
                    const textNodes = [];
                    const walker = document.createTreeWalker(
                        suggestionContent,
                        NodeFilter.SHOW_TEXT,
                        null,
                        false
                    );
                    let node;
                    while (node = walker.nextNode()) {
                        const text = node.textContent.trim();
                        if (text) {
                            textNodes.push(text);
                        }
                    }
                    optionText = textNodes.join(', ') || activeOption.textContent.trim() || activeOption.innerText.trim();

                    if (optionText) {
                        // Announce the highlighted option through the announcer
                        accessibilityAnnouncer.addMessage('geocoder-option', optionText);
                    }
                }
            }
        } else {
            addressInput.removeAttribute('aria-activedescendant');
            lastActiveOptionId = null;
            // Clear geocoder option announcement when no option is selected
            accessibilityAnnouncer.removeMessage('geocoder-option');
        }
    };

    updateSuggestionsAriaAttributes();

    const observer = new MutationObserver(() => {
        updateSuggestionsAriaAttributes();
    });

    observer.observe(suggestionsList, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
    });
}

document.querySelectorAll('.store-locator__filters-button').forEach((btn) => {
    btn.addEventListener('click', () => {
        const newFilter = btn.getAttribute('data-filter');
        if (newFilter === activeFilter) return;

        document.querySelectorAll('.store-locator__filters-button').forEach(b => {
            b.classList.remove('store-locator__filters-button--active');
            b.setAttribute('aria-pressed', 'false');
        });

        btn.classList.add('store-locator__filters-button--active');
        btn.setAttribute('aria-pressed', 'true');

        activeFilter = newFilter;
        selectedFeatureId = null;
        if (currentPopup) {
            currentPopup.remove();
            currentPopup = null;
        }
        removeSelectedMarker();
        applyMapFilter();

        if (lastSearchCoords) {
            const nearbyLocations = findNearbyLocations(lastSearchCoords);
            displaySearchResults(nearbyLocations, lastSearchCoords);
        }
    });
});

document.getElementById('use-current-location-btn').addEventListener('click', getUserCurrentLocation);

geocoder.on('results', function (results) {
    const suggestionsList = document.querySelector('.suggestions-wrapper ul');

    // Check if this is a search query (not just suggestions)
    const isSearchQuery = results.query && results.query.length > 0;
    const hasLocationResults = results.features.length > 0;

    if (suggestionsList && !isSearchQuery) {
        // Only announce geocoder suggestions if it's not a search query
        // (search queries will be handled below)
        const count = suggestionsList.children.length;
        const statusMessage = count > 0
            ? announcements.townsRegionsFound(count)
            : announcements.noTownsRegionsFound();

        const geocoderInput = document.querySelector('.mapboxgl-ctrl-geocoder--input');
        if (geocoderInput) {
            geocoderInput.setAttribute('aria-expanded', count > 0 ? 'true' : 'false');
        }

        // Announce through the announcer
        accessibilityAnnouncer.addMessage('geocoder-results', statusMessage);
    }

    if (hasLocationResults) {
        // Clear geocoder-results when we have actual location results
        accessibilityAnnouncer.removeMessage('geocoder-results');

        const firstResult = results.features[0];
        map.flyTo({
            center: firstResult.geometry.coordinates,
            zoom: 14
        });
        const nearbyLocations = findNearbyLocations(firstResult.geometry.coordinates);
        lastSearchCoords = firstResult.geometry.coordinates;
        displaySearchResults(nearbyLocations, firstResult.geometry.coordinates);
    } else if (isSearchQuery) {
        // User searched but no location was found - clear results
        const resultsContainer = getActiveResultsContainer();
        resultsContainer.innerHTML = '';
        const errorMessageElement = document.querySelector('.location-error__message');
        const errorMessage = announcements.noResultsFound();
        errorMessageElement.textContent = errorMessage;

        // Clear ALL previous search-related messages and show only error
        accessibilityAnnouncer.removeMessage('shop-locations');
        accessibilityAnnouncer.removeMessage('geocoder-results');
        accessibilityAnnouncer.removeMessage('location-details');
        accessibilityAnnouncer.removeMessage('geocoder-option');
        accessibilityAnnouncer.addMessage('error', errorMessage);
    }
});

const geocoderInput = document.querySelector('.mapboxgl-ctrl-geocoder--input');
if (geocoderInput) {
    geocoderInput.addEventListener('input', function () {
        this.focus();
        // Check for suggestions after a short delay to allow them to render
        setTimeout(() => {
            const suggestionsList = document.querySelector('.suggestions-wrapper ul');
            if (suggestionsList) {
                const count = suggestionsList.children.length;
                if (count > 0) {
                    const statusMessage = announcements.townsRegionsFound(count);
                    // Announce through the announcer
                    accessibilityAnnouncer.addMessage('geocoder-results', statusMessage);
                } else if (this.value.length > 0) {
                    const statusMessage = announcements.noTownsRegionsFound();
                    // Announce through the announcer
                    accessibilityAnnouncer.addMessage('geocoder-results', statusMessage);
                } else {
                    // Clear geocoder results when input is empty
                    accessibilityAnnouncer.removeMessage('geocoder-results');
                }
            }
        }, 300);
    });

    geocoderInput.addEventListener('keydown', function (e) {
        const suggestionsList = document.querySelector('.suggestions-wrapper ul');
        if (suggestionsList && suggestionsList.children.length > 0) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                setTimeout(() => this.focus(), 0);
            }
        }
    });
}

function findNearbyLocations(coords) {
    const maxDistance = dataMapDistanceUnit === 'miles' ? 10 : 16.09;

    const nearbyLocations = allMarkers.filter(marker => {
        const distance = calculateDistance(coords, [marker.Longitude, marker.Latitude]);
        marker.distanceFromSearch = distance;
        return distance <= maxDistance;
    });

    const filteredByType = nearbyLocations.filter((m) => {
        if (activeFilter === 'ultra') return !!m.ultraAmbassador;
        if (activeFilter === 'non-ultra') return !m.ultraAmbassador;
        return true;
    });

    filteredByType.sort((a, b) => a.distanceFromSearch - b.distanceFromSearch);
    return filteredByType;
}

function getUserCurrentLocation() {
    const errorMessageElement = document.querySelector('.location-error__message');
    errorMessageElement.textContent = '';

    function onSuccess(position) {
        const userCoords = [position.coords.longitude, position.coords.latitude];
        const nearbyLocations = findNearbyLocations(userCoords);
        lastSearchCoords = userCoords;
        displaySearchResults(nearbyLocations, userCoords);
    }

    function showErrorMessage(message) {
        const errorMessageElement = document.querySelector('.location-error__message');
        errorMessageElement.textContent = decodeHtmlEntities(message);
    }

    function onError() {
        showErrorMessage(window.theme.strings.storelocator.locationDenied);
    }

    if (!navigator.geolocation) {
        showErrorMessage(window.theme.strings.storelocator.locationUnsupported);
    } else {
        navigator.geolocation.getCurrentPosition(onSuccess, onError);
    }
}

function createSelectedMarker() {
    const el = document.createElement('div');
    el.style.width = '35px';
    el.style.height = '40px';
    el.style.backgroundImage = `url(${window.theme.store_locator.markerImageSelected})`;
    el.style.backgroundSize = 'contain';
    el.style.backgroundRepeat = 'no-repeat';
    return el;
}

function removeSelectedMarker() {
    if (selectedMarker) {
        selectedMarker.remove();
        selectedMarker = null;
    }
}

function expandStoreItem(storeItem) {
    const locationButton = storeItem.querySelector('.store-item__button');
    const content = storeItem.querySelector('.content');
    const isUltraAmbassador = storeItem.classList.contains('ultra-ambassador');

    closeAllStoreItems();
    storeItem.classList.add('active');
    if (locationButton) {
        locationButton.setAttribute('aria-expanded', 'true');
    }

    const lat = parseFloat(storeItem.getAttribute('data-lat'));
    const lng = parseFloat(storeItem.getAttribute('data-lng'));
    const coords = [lng, lat];

    map.flyTo({ center: coords });
    showLocationPopup(coords);
    content.style.display = 'block';

    if (selectedMarker) {
        selectedMarker.remove();
    }

    if (!isUltraAmbassador) {
        const markerElement = createSelectedMarker();
        selectedMarker = new mapboxgl.Marker(markerElement).setLngLat(coords).addTo(map);
    }

    setTimeout(() => {
        if (locationButton) {
            locationButton.focus();
        } else {
            storeItem.focus();
        }
    }, 100);

    const locationName = storeItem.querySelector('h4').textContent;
    accessibilityAnnouncer.addMessage('location-details', `${locationName} ${announcements.detailsExpanded()}`);
}

function collapseStoreItem(storeItem) {
    const locationButton = storeItem.querySelector('.store-item__button');
    const content = storeItem.querySelector('.content');

    content.style.display = 'none';
    storeItem.classList.remove('active');
    if (locationButton) {
        locationButton.setAttribute('aria-expanded', 'false');
    }

    if (currentPopup) {
        currentPopup.remove();
    }
    removeSelectedMarker();

    const container = storeItem.closest('ul');
    if (container) {
        container.focus();
    }

    const locationName = storeItem.querySelector('h4').textContent;
    accessibilityAnnouncer.addMessage('location-details', `${locationName} ${announcements.detailsCollapsed()}`);
}

// Legacy function for backwards compatibility - now uses the announcer class
function announceToScreenReader(message) {
    accessibilityAnnouncer.addMessage('general', message);
}

function displaySearchResults(locations, userCoords) {
    const resultsContainer = getActiveResultsContainer();
    const fragment = document.createDocumentFragment();

    resultsContainer.innerHTML = '';

    const filteredLocations = (locations || []).filter((m) => {
        if (activeFilter === 'ultra') return !!m.ultraAmbassador;
        if (activeFilter === 'non-ultra') return !m.ultraAmbassador;
        return true;
    });

    if (!filteredLocations.length) {
        addressInput.setAttribute('aria-expanded', 'false');

        // Update error message (not a live region, just for visual reference)
        const errorMessageElement = document.querySelector('.location-error__message');
        const errorMessage = announcements.noResultsFound();
        errorMessageElement.textContent = errorMessage;

        // Clear previous search-related messages and show only error
        accessibilityAnnouncer.removeMessage('shop-locations');
        accessibilityAnnouncer.removeMessage('geocoder-results');
        accessibilityAnnouncer.removeMessage('location-details');
        accessibilityAnnouncer.addMessage('error', errorMessage);

        resultsContainer.removeAttribute('role');
        resultsContainer.removeAttribute('aria-label');
        resultsContainer.removeAttribute('tabindex');
    } else {
        addressInput.setAttribute('aria-expanded', 'true');

        // Clear error message when results are found
        const errorMessageElement = document.querySelector('.location-error__message');
        errorMessageElement.textContent = '';

        // Clear error and geocoder results, show shop locations
        accessibilityAnnouncer.removeMessage('error');
        accessibilityAnnouncer.removeMessage('geocoder-results');
        accessibilityAnnouncer.addMessage('shop-locations', announcements.shopLocationsFound(filteredLocations.length));

        resultsContainer.setAttribute('role', 'list');
        resultsContainer.setAttribute('aria-label', announcements.storeLocationsFound(filteredLocations.length));
        resultsContainer.setAttribute('tabindex', '0');

        filteredLocations.forEach((location, index) => {
            const googleMapsURL = `https://www.google.com/maps/dir/?api=1&destination=${location.Latitude},${location.Longitude}`;
            const distance = calculateDistance(userCoords, [location.Longitude, location.Latitude]);
            const resultItem = document.createElement('li');
            const locationButton = document.createElement('button');

            resultItem.classList.add('store-item');
            locationButton.classList.add('store-item__button');
            locationButton.setAttribute('type', 'button');
            locationButton.setAttribute('aria-expanded', 'false');
            locationButton.setAttribute('tabindex', '-1');
            const distanceUnit = dataMapDistanceUnit === 'miles' ? 'miles' : 'kilometers';
            const locationLabel = announcements.locationAway(
                location.Name,
                distance.toFixed(2),
                distanceUnit,
                location.ultraAmbassador
            ) + '. ' + announcements.pressToExpand();
            locationButton.setAttribute('aria-label', locationLabel);

            if (location.ultraAmbassador) {
                resultItem.classList.add('ultra-ambassador');
            }

            resultItem.dataset.lng = location.Longitude;
            resultItem.dataset.lat = location.Latitude;
            resultItem.dataset.id = location.id;

            locationButton.innerHTML = `
                <div class="location-item">
                    ${location.ultraAmbassador ? '<div class="ultra-banner" aria-hidden="true">Vuse Ultra</div>' : ''}
                    <div class="location-item__content">
                        <div class="title-wrapper">
                            <h4>${location.Name}</h4>
                        </div>
                        <p>${distance.toFixed(2)} ${dataMapDistanceUnit === 'miles' ? window.theme.strings.storelocator.distanceUnitMiles : window.theme.strings.storelocator.distanceUnitKm} ${window.theme.strings.storelocator.distanceText}</p>
                        <div class="content">
                            <p class="address">${location['Address Line 1']} ${location['Address Line 2'] ? ', ' + location['Address Line 2'] : ''}${location['City/Town']}, ${location['Postal Code']}
                            </p>
                            <div class="store-locator-products"><p>${window.theme.strings.storelocator.availableProductText}</p></div>
                            <div><a class="btn btn-primary get-directions" href="${googleMapsURL}" target="_blank">${window.theme.strings.storelocator.direction}</a></div>
                        </div>
                    </div>
                </div>
            `;

            resultItem.appendChild(locationButton);
            fragment.appendChild(resultItem);
        });
    }

    resultsContainer.appendChild(fragment);
    initializeResultsKeyboardNavigation();
}

function initializeResultsKeyboardNavigation() {
    const resultsContainers = [
        document.getElementById('search-results'),
        document.getElementById('search-results-mobile')
    ];

    resultsContainers.forEach((container) => {
        if (!container) return;

        const newContainer = container.cloneNode(true);
        container.parentNode.replaceChild(newContainer, container);

        let activeIndex = -1;

        newContainer.addEventListener('keydown', (e) => {
            const items = Array.from(newContainer.querySelectorAll('.store-item'));
            if (items.length === 0) return;

            const focusOnContainer = document.activeElement === newContainer;
            const focusOnButton = document.activeElement.classList.contains('store-item__button');
            const focusOnLink = document.activeElement.classList.contains('get-directions');

            if (!focusOnContainer && !focusOnButton && !focusOnLink && document.activeElement.closest('.content')) {
                return;
            }

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    if (activeIndex < items.length - 1) {
                        activeIndex++;
                    } else {
                        activeIndex = 0;
                    }
                    updateFocusedStoreItem(items, activeIndex);
                    break;

                case 'ArrowUp':
                    e.preventDefault();
                    if (activeIndex > 0) {
                        activeIndex--;
                    } else {
                        activeIndex = items.length - 1;
                    }
                    updateFocusedStoreItem(items, activeIndex);
                    break;

                case 'Enter':
                case ' ':
                    if (focusOnContainer || focusOnButton || focusOnLink) {
                        e.preventDefault();
                        if (activeIndex >= 0 && activeIndex < items.length) {
                            const currentItem = items[activeIndex];
                            if (currentItem.classList.contains('active')) {
                                collapseStoreItem(currentItem);
                            } else {
                                expandStoreItem(currentItem);
                            }
                        }
                    }
                    break;

                case 'Escape':
                    if (activeIndex >= 0 && items[activeIndex].classList.contains('active')) {
                        e.preventDefault();
                        collapseStoreItem(items[activeIndex]);
                    }
                    break;
            }
        });

        newContainer.addEventListener('click', function (event) {
            // Handle clicks on the button or its children
            const locationButton = event.target.closest('.store-item__button');
            const storeItem = locationButton ? locationButton.closest('.store-item') : event.target.closest('.store-item');
            if (!storeItem) return;

            const items = Array.from(newContainer.querySelectorAll('.store-item'));
            activeIndex = items.indexOf(storeItem);

            if (storeItem.classList.contains('active')) {
                collapseStoreItem(storeItem);
                return;
            }

            expandStoreItem(storeItem);
        });

        newContainer.addEventListener('focus', () => {
            const items = Array.from(newContainer.querySelectorAll('.store-item'));
            if (items.length > 0 && activeIndex === -1) {
                activeIndex = 0;
                updateFocusedStoreItem(items, activeIndex);
            }
        });
    });
}

function updateFocusedStoreItem(items, index) {
    items.forEach((item, i) => {
        const locationButton = item.querySelector('.store-item__button');
        if (i === index) {
            if (locationButton) {
                locationButton.setAttribute('tabindex', '0');
                locationButton.focus();
            } else {
                item.setAttribute('tabindex', '0');
                item.focus();
            }
            item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } else {
            if (locationButton) {
                locationButton.setAttribute('tabindex', '-1');
            } else {
                item.setAttribute('tabindex', '-1');
            }
        }
    });
}

function closeAllStoreItems() {
    document.querySelectorAll('.store-item').forEach(storeItem => {
        const content = storeItem.querySelector('.content');
        const locationButton = storeItem.querySelector('.store-item__button');
        content.style.display = 'none';
        storeItem.classList.remove('active');
        if (locationButton) {
            locationButton.setAttribute('aria-expanded', 'false');
        }
    });
}

function showLocationPopup(coords) {
    if (currentPopup) {
        currentPopup.remove();
    }
    removeSelectedMarker();

    const marker = allMarkers.find(marker => marker.Latitude === coords[1] && marker.Longitude === coords[0]);
    const googleMapsURL = `https://www.google.com/maps/dir/?api=1&destination=${coords[1]},${coords[0]}`;
    const ultraBanner = marker.ultraAmbassador ? '<div class="ultra-banner">Vuse Ultra</div>' : '';
    const popupHTML = `
        <div class="location-popup">
            ${ultraBanner}
            <div class="location-popup__content">
                <strong>${marker.Name}</strong><br>
                <p>${marker['Address Line 1']} ${marker['Address Line 2'] ? ', ' + marker['Address Line 2'] : ''},${marker['City/Town']},${marker['Postal Code']}</p>
                <button class="btn btn-primary direction" onclick="window.open('${googleMapsURL}', '_blank')">${window.theme.strings.storelocator.direction}</button>
            </div>
        </div>
    `;

    currentPopup = new mapboxgl.Popup()
        .setLngLat(coords)
        .setHTML(popupHTML)
        .addTo(map);

    currentPopup.on('close', function () {
        removeSelectedMarker();
    });
}

function degreesToRadians(degrees) {
    return degrees * Math.PI / 180;
}

function calculateDistance(coord1, coord2) {
    const earthRadiusKm = 6371;
    const latDifference = degreesToRadians(coord2[1] - coord1[1]);
    const lonDifference = degreesToRadians(coord2[0] - coord1[0]);

    const a = Math.sin(latDifference / 2) * Math.sin(latDifference / 2) +
        Math.cos(degreesToRadians(coord1[1])) * Math.cos(degreesToRadians(coord2[1])) *
        Math.sin(lonDifference / 2) * Math.sin(lonDifference / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    let distance = earthRadiusKm * c;

    if (dataMapDistanceUnit === 'miles') {
        distance *= 0.621371;
    }

    return distance;
}

let resizeDebounceTimer;
window.addEventListener('resize', function () {
    clearTimeout(resizeDebounceTimer);
    resizeDebounceTimer = setTimeout(() => {
        const currentActiveContainer = getActiveResultsContainer();
        if (currentActiveContainer !== activeResultsContainer) {
            transferResultsBetweenContainers();
            activeResultsContainer = currentActiveContainer;
        }
    }, 100);
});

function decodeHtmlEntities(encodedString) {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = encodedString;
    return textArea.textContent;
}

function transferResultsBetweenContainers() {
    const sourceContainer = activeResultsContainer;
    const targetContainer = sourceContainer === document.getElementById('search-results')
        ? document.getElementById('search-results-mobile')
        : document.getElementById('search-results');

    while (sourceContainer.firstChild) {
        targetContainer.appendChild(sourceContainer.firstChild);
    }
}